import {
  CritereKey,
  CritereNote,
  CritereTechnique,
  CommandeAnalyse,
  TechNotes,
} from './types';
import { isOffreValide, isAccepte, isPending } from './statutOffre';

// Rappel des criteres de notation en vigueur dans l'accord-cadre (reference contractuelle figee).
// Source unique reutilisee par l'onglet Analyse (memo en tete) ET le Descriptif procedural (modale).
export const CRITERES_MEMO = `Pour mémoire, les critères en vigueur dans l'accord-cadre sont les suivants :
• Sur 4 points, Présentation des moyens humains et matériels dédiés au marché (de 3000 caractères maximum) : Le critère relatif aux moyens humains et matériels dédiés au marché vise à apprécier la capacité du candidat à mobiliser des ressources adaptées, suffisantes et opérationnelles pour garantir la bonne exécution des prestations. À ce titre, il sera notamment tenu compte, d'une part, de la qualité des moyens humains proposés, au regard de la composition de l'équipe, des compétences, qualifications et expériences des intervenants, ainsi que de leur niveau d'implication et des modalités d'encadrement. D'autre part, seront évalués les moyens matériels et techniques, en particulier leur nature, leur adéquation aux besoins du marché, ainsi que les dispositifs de maintenance et les outils de suivi associés. Enfin, une attention pourra être portée à la méthodologie de mobilisation des moyens, notamment la cohérence d'ensemble du dispositif proposé, la capacité à faire face aux aléas et les garanties apportées en matière de continuité de service.
• Sur 5 points, Présentation des modes opératoires et organisationnels pour l'exécution du marché (3000 caractères maximum) : Le critère mode opératoire et organisationnel a pour objectif d'évaluer la pertinence et la cohérence de l'organisation proposée par le candidat pour la réalisation du marché. Il prendra en compte la clarté et l'efficacité du plan d'action et des procédures opérationnelles, la répartition des rôles et responsabilités au sein de l'équipe, ainsi que les dispositifs de coordination, de suivi et de pilotage des prestations. Seront également analysées la méthodologie appliquée au marché, la capacité à anticiper et gérer les aléas, ainsi que la pertinence des outils de reporting et de communication prévue pour assurer le respect des délais, de la qualité et des exigences contractuelles.
• Sur 1 point, présentation des mesures en matière environnementale et en matière sociale (3000 caractères maximum) : Valeur environnementale et sociale de l'offre, appréciée au regard des mesures proposées en matière de 1) gestion et de réduction des déchets (tri, recyclage, valorisation…) et de rejet de CO2, 2) l'engagement du candidat en faveur de l'emploi des publics suivants : jeunes de moins de 26 ans, ou demandeurs d'emploi de longue durée, ou personnes en situation de handicap, notamment par le biais de recrutements directs, de stages ou de missions d'immersion professionnelle dans le cadre de l'exécution du marché.`;

// Bareme technique MS (verbatim depuis AnalyseOffreTechnique l.57-97). Total maxPoints =
// 5 + 4 + 1 = 10 -> la note technique est naturellement sur 10.
export const CRITERES_TECHNIQUES: CritereTechnique[] = [
  {
    key: 'organisation',
    label: "Modes Opératoires pour l'éxecution du marché",
    maxPoints: 5,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1.25, label: '1,25 - Pas satisfaisant' },
      { value: 2.5, label: '2,50 - Peu satisfaisant' },
      { value: 3.75, label: '3,75 - Assez satisfaisant' },
      { value: 5, label: '5,00 - Satisfaisant' },
    ],
    description:
      "Évaluation des méthodologies, processus et approches proposés par le prestataire pour l'exécution du marché. Cette section examine la cohérence et l'efficacité des méthodes de travail, l'organisation du chantier, la gestion des interfaces et la qualité de l'encadrement.",
  },
  {
    key: 'moyens',
    label: 'Moyens Humains et Materiels dédiés au marché',
    maxPoints: 4,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1, label: '1,00 - Pas satisfaisant' },
      { value: 2, label: '2,00 - Peu satisfaisant' },
      { value: 3, label: '3,00 - Assez satisfaisant' },
      { value: 4, label: '4,00 - Satisfaisant' },
    ],
    description:
      "Évaluation des ressources humaines (équipes, compétences, qualifications) et matérielles (équipements, outils, logiciels) que le prestataire propose de mobiliser pour l'exécution du marché. Cette section examine l'adéquation des moyens avec les exigences techniques et le calendrier du projet.",
  },
  {
    key: 'social',
    label: 'Mesures en matière Environnementale & Sociale',
    maxPoints: 1,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 0.25, label: '0,25 - Pas satisfaisant' },
      { value: 0.5, label: '0,50 - Peu satisfaisant' },
      { value: 0.75, label: '0,75 - Assez satisfaisant' },
      { value: 1, label: '1,00 - Satisfaisant' },
    ],
    description:
      "Évaluation des engagements et dispositifs proposés par le prestataire en matière de responsabilité environnementale (gestion des déchets, réduction des nuisances, économies d'énergie) et sociale (insertion, conditions de travail, sous-traitance responsable).",
  },
];

const MAX_POSSIBLE_POINTS = CRITERES_TECHNIQUES.reduce((sum, c) => sum + c.maxPoints, 0);

// Note technique globale sur 10 (verbatim : (totalPoints / maxPossiblePoints) * 10).
// Une note absente compte pour 0. Renvoie un NOMBRE ; le formatage est separe.
export const computeNoteGlobale = (
  notes?: Partial<Record<CritereKey, CritereNote>>,
): number => {
  if (!notes) return 0;
  // `|| 0` (et non `?? 0`) : une note NaN eventuelle est ramenee a 0 et ne pollue pas le
  // score (comportement verbatim du legacy getNoteGlobale).
  const totalPoints = CRITERES_TECHNIQUES.reduce((sum, c) => sum + (notes[c.key]?.note || 0), 0);
  return MAX_POSSIBLE_POINTS > 0 ? (totalPoints / MAX_POSSIBLE_POINTS) * 10 : 0;
};

// Presentation fr-FR a 2 decimales (helper d'affichage, hors calcul).
export const formatNoteGlobale = (note: number): string =>
  note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Seed du buffer editable depuis l'API (commande.note_technique), keye par String(commande.id).
// Mapping de domaine : organisation->operationnel, moyens->humain, social->environnement.
export const seedTechNotesFromCommandes = (commandes: CommandeAnalyse[]): TechNotes => {
  const seed: TechNotes = {};
  commandes.forEach((cmd) => {
    const nt = cmd.note_technique;
    if (!nt || cmd.id === undefined) return;
    const expired = !isOffreValide(cmd.id_status);
    seed[String(cmd.id)] = {
      organisation: {
        note: nt.note_operationnel ?? 0,
        commentaire: nt.commentaire_operationnel || '',
        description: nt.description_operationnel || '',
        isExpired: expired,
      },
      moyens: {
        note: nt.note_humain ?? 0,
        commentaire: nt.commentaire_humain || '',
        description: nt.description_humain || '',
        isExpired: expired,
      },
      social: {
        note: nt.note_environnement ?? 0,
        commentaire: nt.commentaire_environnement || '',
        description: nt.description_environnement || '',
        isExpired: expired,
      },
    };
  });
  return seed;
};

// Une offre est-elle a noter ? On saute les offres hors-lice (refus/expire) et celles deja
// figees (acceptee/en signature). Predicat unique reutilise par la validation et le rendu.
export const isOffreANoter = (idStatus?: number): boolean =>
  isOffreValide(idStatus) && !isAccepte(idStatus) && !isPending(idStatus);

export interface NotationFieldError {
  note?: string;
  commentaire?: string;
}
export type NotationErrors = Record<string, Partial<Record<CritereKey, NotationFieldError>>>;
export interface NotationValidation {
  valid: boolean;
  errors: NotationErrors;
  firstErrorId: string | null;
}

const ERR_NOTE = 'La note est requise';
const ERR_COMMENTAIRE = 'Le commentaire est requis';

// Pour chaque offre a noter : chaque critere exige une note definie et un commentaire non vide.
// L'id d'element (pour le scroll) est construit cote React ; ici on renvoie l'id de commande.
export const validateNotation = (
  commandes: CommandeAnalyse[],
  techNotes: TechNotes,
): NotationValidation => {
  const errors: NotationErrors = {};
  let firstErrorId: string | null = null;

  commandes.forEach((cmd) => {
    if (cmd.id === undefined || !isOffreANoter(cmd.id_status)) return;
    const key = String(cmd.id);
    const notes = techNotes[key] || {};
    CRITERES_TECHNIQUES.forEach((critere) => {
      const entry = notes[critere.key];
      const fieldErrors: NotationFieldError = {};
      if (entry?.note === undefined || Number.isNaN(entry.note)) {
        fieldErrors.note = ERR_NOTE;
      }
      if (!entry?.commentaire?.trim()) {
        fieldErrors.commentaire = ERR_COMMENTAIRE;
      }
      if (fieldErrors.note || fieldErrors.commentaire) {
        if (!errors[key]) errors[key] = {};
        errors[key][critere.key] = fieldErrors;
        if (!firstErrorId) firstErrorId = key;
      }
    });
  });

  return { valid: firstErrorId === null, errors, firstErrorId };
};
